import * as THREE from 'three';

export type Vec3 = [number, number, number];

export interface NetworkNode {
  position: THREE.Vector3;
  /** Index into CLUSTERS, or -1 for a system node. */
  cluster: number;
  /** Index into SYSTEMS, or -1 for a cluster node. */
  system: number;
  /** 0..1 stagger used while the node is revealed. */
  order: number;
  /** True for the handful of nodes that anchor Act IV. */
  hub: boolean;
}

export interface Segment {
  a: number;
  b: number;
  /** 0..1 stagger used while the segment is revealed. */
  order: number;
}

export interface ClusterSpec {
  label: string;
  center: Vec3;
  radius: number;
  count: number;
  /** "ring" wraps the points around the bottle; "blob" is a loose sphere. */
  shape: 'ring' | 'blob';
  labelOffset: Vec3;
}

export interface SystemSpec {
  label: string;
  position: Vec3;
  /** Clusters this system connects into. */
  links: number[];
  labelOffset: Vec3;
}

/** Positions are in bottle space: the bottle stands on y = 0 at the origin. */
export const CLUSTERS: ClusterSpec[] = [
  {
    label: 'Product',
    center: [-1.08, 1.75, 0.05],
    radius: 0.5,
    count: 18,
    shape: 'blob',
    labelOffset: [-0.12, 0.48, 0],
  },
  {
    label: 'Customer',
    center: [1.0, 2.0, 0.05],
    radius: 0.5,
    count: 16,
    shape: 'blob',
    labelOffset: [0.1, 0.5, 0],
  },
  {
    label: 'Sales',
    center: [1.25, 0.75, 0.2],
    radius: 0.48,
    count: 16,
    shape: 'blob',
    labelOffset: [0.12, -0.46, 0],
  },
  {
    label: 'Inventory',
    center: [-1.08, 0.6, 0.2],
    radius: 0.48,
    count: 14,
    shape: 'blob',
    labelOffset: [-0.12, -0.46, 0],
  },
];

export const SYSTEMS: SystemSpec[] = [
  { label: 'Commerce', position: [-0.3, 2.35, -1.55], links: [0, 2], labelOffset: [0, 0.3, 0] },
  { label: 'Marketing', position: [-2.05, 2.2, -1.05], links: [1], labelOffset: [0, 0.3, 0] },
  { label: 'POS', position: [-2.1, 1.05, -1.25], links: [1, 3], labelOffset: [0, 0.3, 0] },
  { label: 'ERP', position: [2.0, 0.42, -1.15], links: [3, 0], labelOffset: [0, -0.3, 0] },
  { label: 'Planning', position: [2.05, 2.1, -0.65], links: [2, 0], labelOffset: [0, 0.3, 0] },
];

/** Small deterministic PRNG so the layout is stable between renders. */
const mulberry32 = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export interface NetworkLayout {
  nodes: NetworkNode[];
  /** Act II: fine lines inside each cluster. */
  clusterLines: Segment[];
  /** Act III: lines between clusters and out to systems. */
  connections: Segment[];
  /** Act IV: the handful of pathways that matter. */
  pathways: Segment[];
  /** Node index of the product hub every pathway leads to. */
  productHub: number;
  /** Node index per system, in SYSTEMS order. */
  systemNodes: number[];
  /** Node index of the hub per cluster, in CLUSTERS order. */
  clusterHubs: number[];
}

export const buildNetwork = (): NetworkLayout => {
  const random = mulberry32(20261011);
  const nodes: NetworkNode[] = [];
  const clusterMembers: number[][] = [];

  CLUSTERS.forEach((cluster, clusterIndex) => {
    const members: number[] = [];
    for (let index = 0; index < cluster.count; index += 1) {
      const position = new THREE.Vector3();
      if (cluster.shape === 'ring') {
        const angle = random() * Math.PI * 2;
        const radius = cluster.radius * (0.82 + random() * 0.36);
        position.set(
          Math.cos(angle) * radius,
          cluster.center[1] + (random() - 0.5) * 1.9,
          Math.sin(angle) * radius,
        );
      } else {
        const u = random() * 2 - 1;
        const theta = random() * Math.PI * 2;
        const r = cluster.radius * Math.cbrt(random());
        const s = Math.sqrt(1 - u * u);
        position.set(
          cluster.center[0] + s * Math.cos(theta) * r,
          cluster.center[1] + u * r * 0.8,
          cluster.center[2] + s * Math.sin(theta) * r,
        );
      }
      members.push(nodes.length);
      nodes.push({
        position,
        cluster: clusterIndex,
        system: -1,
        order: clusterIndex / CLUSTERS.length + (random() * 0.9) / CLUSTERS.length,
        hub: false,
      });
    }
    clusterMembers.push(members);
  });

  const systemNodes = SYSTEMS.map((system, systemIndex) => {
    nodes.push({
      position: new THREE.Vector3(...system.position),
      cluster: -1,
      system: systemIndex,
      order: systemIndex / SYSTEMS.length,
      hub: true,
    });
    return nodes.length - 1;
  });

  const clusterHubs = CLUSTERS.map((cluster, clusterIndex) => {
    const center = new THREE.Vector3(...cluster.center);
    const members = clusterMembers[clusterIndex];
    const scored = members
      .map((index) => ({ index, distance: nodes[index].position.distanceTo(center) }))
      .sort((a, b) => a.distance - b.distance);
    // For the ring, prefer a node in front of the bottle so pathways read clearly.
    const choice =
      cluster.shape === 'ring'
        ? members.reduce((best, index) =>
            nodes[index].position.z > nodes[best].position.z ? index : best,
          )
        : scored[0].index;
    nodes[choice].hub = true;
    return choice;
  });

  const seen = new Set<string>();
  const addSegment = (list: Segment[], a: number, b: number, order: number) => {
    const key = a < b ? `${a}-${b}` : `${b}-${a}`;
    if (a === b || seen.has(key)) return;
    seen.add(key);
    list.push({ a, b, order });
  };

  const clusterLines: Segment[] = [];
  clusterMembers.forEach((members, clusterIndex) => {
    members.forEach((index) => {
      const nearest = members
        .filter((other) => other !== index)
        .map((other) => ({ other, distance: nodes[index].position.distanceTo(nodes[other].position) }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 2);
      nearest.forEach(({ other }) => {
        addSegment(
          clusterLines,
          index,
          other,
          clusterIndex / CLUSTERS.length + (random() * 0.8) / CLUSTERS.length,
        );
      });
    });
  });

  const connections: Segment[] = [];
  const pairs: [number, number][] = [
    [0, 1],
    [0, 2],
    [0, 3],
    [1, 3],
    [2, 0],
    [1, 2],
  ];
  pairs.forEach(([from, to], pairIndex) => {
    for (let count = 0; count < 2; count += 1) {
      const a = clusterMembers[from][Math.floor(random() * clusterMembers[from].length)];
      const b = clusterMembers[to][Math.floor(random() * clusterMembers[to].length)];
      addSegment(connections, a, b, 0.05 + (pairIndex / pairs.length) * 0.45 + random() * 0.08);
    }
  });
  SYSTEMS.forEach((system, systemIndex) => {
    system.links.forEach((clusterIndex) => {
      const members = clusterMembers[clusterIndex];
      for (let count = 0; count < 2; count += 1) {
        const target = members[Math.floor(random() * members.length)];
        addSegment(
          connections,
          systemNodes[systemIndex],
          target,
          0.5 + (systemIndex / SYSTEMS.length) * 0.4 + random() * 0.1,
        );
      }
    });
  });

  const productHub = clusterHubs[0];
  const pathways: Segment[] = [];
  const pathwaySeen = new Set<string>();
  const addPathway = (a: number, b: number, order: number) => {
    const key = `${a}-${b}`;
    if (pathwaySeen.has(key)) return;
    pathwaySeen.add(key);
    pathways.push({ a, b, order });
  };
  addPathway(clusterHubs[1], productHub, 0.05);
  addPathway(clusterHubs[2], productHub, 0.15);
  addPathway(clusterHubs[3], productHub, 0.25);
  addPathway(systemNodes[1], clusterHubs[1], 0.4);
  addPathway(systemNodes[2], clusterHubs[3], 0.5);
  addPathway(systemNodes[3], clusterHubs[3], 0.6);
  addPathway(systemNodes[0], clusterHubs[2], 0.7);
  addPathway(systemNodes[4], clusterHubs[2], 0.8);

  return { nodes, clusterLines, connections, pathways, productHub, systemNodes, clusterHubs };
};

/**
 * Lines that draw themselves in. Each segment carries an `order` (0..1) and
 * each vertex a `t` (0 at the start, 1 at the end). Fragments past the revealed
 * portion are discarded, so segments grow from their start point.
 */
export const createRevealLineMaterial = (hex: string, opacity: number) =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uColor: { value: new THREE.Color(hex).convertLinearToSRGB() },
      uOpacity: { value: opacity },
      uReveal: { value: 0 },
      uWindow: { value: 0.25 },
    },
    vertexShader: /* glsl */ `
      attribute float aOrder;
      attribute float aT;
      varying float vOrder;
      varying float vT;
      void main() {
        vOrder = aOrder;
        vT = aT;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uReveal;
      uniform float uWindow;
      varying float vOrder;
      varying float vT;
      void main() {
        float local = clamp((uReveal - vOrder) / uWindow, 0.0, 1.0);
        if (vT > local) discard;
        gl_FragColor = vec4(uColor, uOpacity * smoothstep(0.0, 0.2, local));
      }
    `,
  });

export const buildSegmentGeometry = (nodes: NetworkNode[], segments: Segment[]) => {
  const positions = new Float32Array(segments.length * 6);
  const orders = new Float32Array(segments.length * 2);
  const ts = new Float32Array(segments.length * 2);

  segments.forEach((segment, index) => {
    const a = nodes[segment.a].position;
    const b = nodes[segment.b].position;
    positions.set([a.x, a.y, a.z, b.x, b.y, b.z], index * 6);
    orders.set([segment.order, segment.order], index * 2);
    ts.set([0, 1], index * 2);
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aOrder', new THREE.BufferAttribute(orders, 1));
  geometry.setAttribute('aT', new THREE.BufferAttribute(ts, 1));
  return geometry;
};

/** Crisp uppercase label rendered to a sprite texture. */
export const createLabelTexture = (text: string, color = '#2b3138') => {
  const scale = 2;
  const width = 320;
  const height = 72;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const context = canvas.getContext('2d');
  if (context) {
    context.scale(scale, scale);
    context.clearRect(0, 0, width, height);
    context.font = "600 26px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = color;
    const letters = text.toUpperCase().split('');
    const spacing = 4;
    const total = letters.reduce((sum, letter) => sum + context.measureText(letter).width + spacing, -spacing);
    let x = width / 2 - total / 2;
    letters.forEach((letter) => {
      const w = context.measureText(letter).width;
      context.fillText(letter, x + w / 2, height / 2 + 1);
      x += w + spacing;
    });
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return { texture, aspect: width / height };
};
